import { Injectable } from '@angular/core'
import { Observable, BehaviorSubject, forkJoin } from 'rxjs'
import { AdTargeting } from './ad-config'
import { Node } from '../../models/node.model'
import { AdComponent } from '../ad/ad.component'

export type ContentTargeting = Pick<AdTargeting, 'categorySlugs' | 'contentIds'>

export function mapToAdTargeting(
  content: Pick<Node, 'aggregated_node_ids' | 'aggregated_category_slugs'>,
  nestedContentTargeting: ContentTargeting | undefined,
  isPremium: boolean
): AdTargeting {
  // Take default targeting unless nested content on the page has additional targeting
  return nestedContentTargeting?.contentIds === undefined
    ? {
        contentIds: content && content.aggregated_node_ids,
        categorySlugs: content && content.aggregated_category_slugs,
        isPremium
      }
    : {
        contentIds: nestedContentTargeting.contentIds,
        categorySlugs: nestedContentTargeting.categorySlugs,
        isPremium
      }
}

/**
 * This service includes methods to update AdTargeting for adSlots that are not destroyed
 * Specifically for nested content within router-outlets, ex: Event hubs or
 * when the router url changes its id (playing?=) but the page component does not destroy the view.
 */
@Injectable({
  providedIn: 'root'
})
export class UpdateContentTargetingService {
  // Stores AdTargeting information related to the content, not the user
  private readonly nestedContentTargetingSource = new BehaviorSubject<ContentTargeting | undefined>(undefined)
  public nestedContentTargeting$: Observable<ContentTargeting | undefined> =
    this.nestedContentTargetingSource.asObservable()

  /**
   * This method allows nested content within router-outlets to update
   * AdTargeting information on parent components. Used on collections and event hubs.
   *
   * @example A user visits the Event Hub or Collection page
   * The user will see one set of ads. If the user then clicks a video,
   * the route will not change, but the video has it's own AdTargeting
   * that needs to be sent when requesting new ads.
   */
  public updateContentTargeting(targeting: ContentTargeting | undefined): void {
    this.nestedContentTargetingSource.next(targeting)
  }

  /**
   * Update an array of loadedAds (within the view) with new targeting
   *
   * @example A user navigates to an article and all the ad slots load. The user clicks related content,
   * and remains on the articles page. The ads will not update with new targeting unless we grab a reference
   * to all the ad slots on the page, map over them and update their targeting.
   */
  public updateLoadedAdsTargeting(targeting: AdTargeting, loadedAds: ReadonlyArray<AdComponent>): Observable<void[]> {
    return forkJoin(loadedAds.map(ad => ad.updateTargeting(targeting)))
  }

  /**
   * Refresh an array of loadedAds (within the view)
   *
   * @example A user navigates to an article and all the ad slots load. The user clicks related content,
   * and remains on the articles page. The ads will not update with new targeting unless we grab a reference
   * to all the ad slots on the page, map over them and call refresh. This will make an `ads?` call in the network.
   */
  public refreshLoadedAds(loadedAds: ReadonlyArray<AdComponent>): Observable<void[]> {
    return forkJoin(loadedAds.map(ad => ad.refresh()))
  }
}
